



































import { computed, defineComponent, ref, set, watch } from "@vue/composition-api";
import { InputPayeeInformation, PayeeInformation } from "@scrile/api-provider/dist/api/PayeeInformationProvider";
import { PayoutSystemSettings } from "@scrile/api-provider/dist/api/PayoutSystemSettingsProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import { FormValues, ListFields } from "@/types";
import BaseSelect from "@/components/BaseSelect";
import BaseSpinner from "@/components/BaseSpinner";
import getComponent, { getListFields } from "../../views/PagePayeeInfo/integrations";

const NOT_CONFIGURED = "not_configured";

export default defineComponent({
  name: "PayoutForm",
  components: { BaseSpinner, BaseSelect },
  setup(_, { emit }) {
    const { authUserStrict } = useAuthUser();
    const loading = ref(false);
    const configuredPayoutSystems = ref<PayoutSystemSettings[]>([]);
    const payeeInformation = ref<PayeeInformation | null>(null);
    const payoutSystemChanged = ref(false);
    const values = ref<FormValues>({
      payoutSystem: NOT_CONFIGURED,
      currency: NOT_CONFIGURED,
    });
    const selectedPayoutSystem = computed(() => values.value.payoutSystem);
    const selectedPayoutSystemConfig = computed(() => getComponent(selectedPayoutSystem.value));
    const payoutSystemForm = computed(() => selectedPayoutSystemConfig.value.component);
    const isPayoutSystemSelected = computed(() => selectedPayoutSystem.value !== NOT_CONFIGURED);
    const hasSavedPayoutSystem = computed(() => !!payeeInformation.value?.payoutSystem);
    const isRequiredPayoutInfoFilled = computed(() => {
      if (!isPayoutSystemSelected.value) {
        return true;
      }
      const systemValues = values.value[selectedPayoutSystem.value] || {};
      return selectedPayoutSystemConfig.value.formFields.every(field => {
        const value = systemValues[field.name];
        return (
          !Array.isArray(field.rules) ||
          !field.rules.includes("required") ||
          (typeof value === "string" ? !!value.trim() : !!value)
        );
      });
    });

    const systemList = computed<ListFields>(() => {
      const configuredSystems = getListFields().filter(
        i => !!configuredPayoutSystems.value.find(s => s.systemName === i.value && s.currencies.length > 0)
      );
      return hasSavedPayoutSystem.value
        ? configuredSystems
        : [{ value: NOT_CONFIGURED, text: $t("Not configured") }, ...configuredSystems];
    });

    const currencyList = computed(() => {
      const selectedSystem = configuredPayoutSystems.value.find(i => i.systemName === selectedPayoutSystem.value);
      return selectedSystem ? selectedSystem.currencies.map(i => ({ value: i, text: i })) : [];
    });

    const input = (newValue: string | null, field: string) => {
      if (field === "payoutSystem" && newValue !== values.value.payoutSystem) {
        payoutSystemChanged.value = true;
      }
      set(values.value, field, newValue);
    };

    const onCurrencyList = (list: { text: string; value: string }[]) => {
      if (!list.find(i => i.value === values.value["currency"])) {
        input(list[0]?.value || NOT_CONFIGURED, "currency");
      }
    };

    const systemInfoInput = (newValues: FormValues) => {
      values.value = { ...values.value, ...newValues };
    };

    const submitPayeeInfo = async () => {
      if (!isRequiredPayoutInfoFilled.value) {
        return;
      }
      if (!isPayoutSystemSelected.value && !payoutSystemChanged.value) {
        return;
      }
      if (!isPayoutSystemSelected.value) {
        payeeInformation.value = await providers.PayeeInformationProvider.update({
          data: { userId: authUserStrict.value.id },
        });
        return;
      }
      const data: InputPayeeInformation = {
        userId: authUserStrict.value.id,
        payoutSystem: values.value.payoutSystem,
        currency: values.value.currency,
        [values.value.payoutSystem]: values.value[values.value.payoutSystem],
      };
      payeeInformation.value = await providers.PayeeInformationProvider.update({ data });
    };

    const getData = async () => {
      try {
        loading.value = true;
        emit("setLoading", loading.value);
        await Promise.all([
          providers.PayoutSystemSettingsProvider.findAll().then(r => (configuredPayoutSystems.value = r)),
          providers.PayeeInformationProvider.find({ id: authUserStrict.value.id }).then(
            r => (payeeInformation.value = r)
          ),
        ]);
        values.value = {
          ...values.value,
          payoutSystem: payeeInformation.value?.payoutSystem ?? NOT_CONFIGURED,
          currency: payeeInformation.value?.currency ?? NOT_CONFIGURED,
        };
      } finally {
        loading.value = false;
        emit("setLoading", loading.value);
      }
    };

    emit("setCb", () => submitPayeeInfo());
    getData();

    watch(currencyList, onCurrencyList);
    watch(isRequiredPayoutInfoFilled, valid => emit("setValid", valid), { immediate: true });

    return { loading, values, payeeInformation, payoutSystemForm, systemList, currencyList, input, systemInfoInput };
  },
});
